import {
  AboutDialogConfiguration
} from '../components/about-dialog/about-dialog.service';
import {
  ModalHeaderType
} from '../components/modal/modal.domain';
import {
  CfrConfig
} from '../domain/address.domain';
import {
  ConsumerType
} from '../domain/consumer-type.domain';
import {
  EservicesConfiguration
} from '../domain/eservices.domain';
import {
  HomePagePrimaryTabConfiguration
} from '../domain/home-page-balance.domain';
import {
  OrderEntryConfiguration,
  OrderStatusConfiguration
} from '../domain/trading.domain';
import {
  BrightcoveConfiguration
} from '../features/brightcove-player/brightcove-player.domain';
import {
  ClientProfileConfiguration
} from '../features/client-profile/client-profile.service';
import {
  ExchangeRates
} from '../features/exchange-rate/exchange-rate.service';
import {
  AnalystRatingConfiguration
} from '../features/markets/financial-widgets/ratings/analyst-rating.component';
import {
  ConfigurationService
} from '../services/configuration/configuration.service';
import {
  TimezoneConfiguration
} from '../services/date/timezone.service';
import {
  ExportConfiguration
} from '../services/export/export.domain';
import {
  FeatureFlags
} from '../services/if-feature/if-feature.service';
import {
  TriggerConfiguration
} from '../services/modal-notification/modal-notification.domain';
import {
  PageTitleServiceConfiguration
} from '../services/page-title.service';
import {
  PreferencesConfiguration
} from '../services/preferences/preferences.service';
import {
  RestFrameworkConfiguration
} from '../services/rest/rest-framework.service';
import {
  SessionConfiguration
} from '../services/session.service';
import {
  StorageKeyBuilderConfiguration
} from '../services/storage/storage-key-builder.service';
import {
  SystemStatusConfiguration
} from '../services/system-status/system-status.domain';
import { UrlUtilityService } from '../utils/url-utility.service';

export class BaseConfig {

  static getLocale(configurationService: ConfigurationService): string {
    return configurationService.getProperty('locale');
  }

  static getBuildTag(configurationService: ConfigurationService): string {
    return configurationService.getProperty('metaInf.buildTag');
  }

  static getLocaleFromLocation(document: Document): 'fr_CA' | 'en_CA' {
    const url = document.location.hostname.toLowerCase();
    const locale = UrlUtilityService.getQueryStringParam(document.location.search, 'locale');
    return locale === 'fr_CA' || url.indexOf('courtierweb') >= 0 ? 'fr_CA' : 'en_CA';
  }

  static getPreferencesConfig(configurationService: ConfigurationService): PreferencesConfiguration {
    return {
      user: configurationService.getProperty('userPreferences'),
      account: configurationService.getProperty('accountPreferences')
    };
  }

  static getStorageKeyBuilderConfig(configurationService: ConfigurationService): StorageKeyBuilderConfiguration {
    const userId = configurationService.getProperty<string>('userId');
    const sessionId = configurationService.getProperty<string>('sessionId');
    return { userId, sessionId };
  }

  static getExportConfig(configurationService: ConfigurationService): ExportConfiguration {
    return {
      exportServiceUrl: configurationService.getProperty('exportServiceUrl'),
      restConsumerName: configurationService.getProperty('restConsumerName')
    };
  }

  static getSessionConfig(): SessionConfiguration {
    return {
      loginUrl: '/',
      logoutUrl: '/logout.html'
    };
  }

  static getRestFrameworkConfig(consumerType: ConsumerType): RestFrameworkConfiguration {
    return {
      restApiVersion: 'v1.0',
      restConsumerName: `com-td-brk-wb-${consumerType.consumerId}`,
      taiAgentHack: true
    };
  }

  static getMenuConfig(configurationService: ConfigurationService): Record<string, unknown> {
    return configurationService.getProperty('menuConfig');
  }

  static getAboutDialogConfig(configurationService: ConfigurationService): AboutDialogConfiguration {
    return {
      version: BaseConfig.getBuildTag(configurationService),
      revision: configurationService.getProperty('metaInf.gitRevision'),
      locationId: configurationService.getProperty('locationId')
    };
  }

  static getClientConfig(configurationService: ConfigurationService): ClientProfileConfiguration {
    return configurationService.getProperty('clientName');
  }

  static getManageSecurityOptionsUrlConfig(configurationService: ConfigurationService): string {
    return configurationService.getProperty('manageSecurityOptionsUrl');
  }

  static getExchangeRates(configurationService: ConfigurationService): ExchangeRates {
    return configurationService.getProperty('fxRates');
  }

  static getHomePageBalanceTrendPrimaryTabEnable(configurationService: ConfigurationService): HomePagePrimaryTabConfiguration {
    return {
      homePageBalanceTrendPrimaryTab: configurationService.getProperty('homePageBalanceTrendPrimaryTabEnable')
    };
  }

  static getBrightcoveConfig(configurationService: ConfigurationService): BrightcoveConfiguration {
    return configurationService.getProperty('brightcoveConfig');
  }

  static getFeatureFlags(configurationService: ConfigurationService): FeatureFlags {
    return configurationService.getProperty('featureFlags');
  }

  static getAnalystRatingConfig(configurationService: ConfigurationService): AnalystRatingConfiguration {
    return {
      newAnalystRatings: configurationService.getProperty('featureFlags.newAnalystRatings'),
      analystRatingLinkId: 'link_markets_research_analysts'
    };
  }

  static getSystemStatusConfig(configurationService: ConfigurationService): SystemStatusConfiguration {
    return {
      systemStatus: configurationService.getProperty('systemStatus'),
      firstStartAfterLogin: false,
      systemDownErrorCode: 'ACCOUNTS.ERROR.ISM.DOWN',
      systemWarningErrorCode: 'UPSTREAM-DATA-MISSING'
    };
  }

  static getEservicesConfig(configurationService: ConfigurationService): EservicesConfiguration {
    return configurationService.getProperty('eservicesConfig');
  }

  static getOrderStatusUserConfig(configurationService: ConfigurationService): OrderStatusConfiguration {
    return configurationService.getProperty('orderStatus');
  }

  static getOrderEntryUserConfig(configurationService: ConfigurationService): OrderEntryConfiguration {
    return {
      mloOrdersEnabled: configurationService.getProperty('mloOrdersEnabled'),
      complexOrdersEnabled: configurationService.getProperty('complexOrdersEnabled'),
      otaOrdersOptionsEnabled: configurationService.getProperty('otaOrdersOptionsEnabled'),
      complexOrdersOptionsEnabled: configurationService.getProperty('complexOrdersOptionsEnabled'),
      accountAndOrderBuyingPowerEnabled: configurationService.getProperty('accountAndOrderBuyingPowerEnabled')
    };
  }

  static getTimezoneConfiguration(configurationService: ConfigurationService): TimezoneConfiguration {
    return {
      timezoneOffsetInMilliseconds: configurationService.getProperty('timezoneOffsetInMillis')
    };
  }

  static getModalNotificationConfiguration(configurationService: ConfigurationService): TriggerConfiguration {
    return configurationService.getProperty('menuConfig.modalItems');
  }

  static getPageTitleConfiguration(configurationService: ConfigurationService): PageTitleServiceConfiguration {
    const titleConfig = configurationService.getProperty('menuConfig.applicationTitle');
    let applicationTitle;
    if (titleConfig !== undefined && Array.isArray(titleConfig) && titleConfig.length >= 0) {
      applicationTitle = titleConfig[0].text;
    }
    return { applicationTitle };
  }

  static getPageOffsetConfiguration(consumerType: ConsumerType): number {
    return consumerType.isEmbedded ? 0 : undefined;
  }

  static getModalHeaderConfig(consumerType: ConsumerType): ModalHeaderType {
    return consumerType.isEmbedded ? ModalHeaderType.LIGHT : ModalHeaderType.DEFAULT;
  }

  static isBrowserUnsupported(browserWindow: Window): boolean {
    const document = browserWindow.document;
    // @ts-expect-error I grant detecting monsters for all eternity of this codebase
    const isIE11 = !!(browserWindow).MSInputMethodContext && !!document.documentMode;
    const isIE10 = (document.all && browserWindow.atob !== undefined);
    const isIE9 = (document.all && !browserWindow.atob);
    const isIE8 = (document.all && !document.addEventListener);
    const isIE7 = (document.all && !document.querySelector);
    // @ts-expect-error if anyone ever fires up IE6 we still want to precisely detect them and launch a hunting team
    const isIE6 = (document.all && !(browserWindow).XMLHttpRequest);
    return isIE6 || isIE7 || isIE8 || isIE9 || isIE10 || isIE11;
  }

  static getCfrConfig(configurationService: ConfigurationService): CfrConfig {
    return configurationService.getProperty('cfrConfig');
  }
}
